import { dayjs } from '@2wunder/klarx-tool'
import { InternalNamePath } from 'antd/lib/form/interface'
import MinimumRentalTime from 'app/components/Core/CategorySegment/MinimumRentalTime'
import KFormItem from 'app/components/Modules/Form/KFormItem'
import { useKFormContext } from 'app/lib/contexts/KFormContext'
import { dateFormat } from 'app/lib/hooks/toolBox'
import { useLazyQuery } from 'app/lib/hooks/useQuery'
import { FETCH_MINIMUM_RENTAL_DATE_QUERY } from 'app/queries/CategorySegment'
import React, { useEffect, useState } from 'react'

import { MemberProps } from '../../convention'

type Props = MemberProps<
  Pick<ICategorySegment, 'minimumReturnDate' | 'minimumRentalDuration' | 'id'>
>

type ErrorInfo = {
  errorFields: Array<{ name: InternalNamePath; errors: string[] }>
}

const MINIMUM_RENTAL_TIME_ERROR = 'Minimum rental time is not met.'
const FIELD_NAME = 'dates'

const MinimumRentalTimeFormItem: React.FC<Props> = ({ member, ...others }) => {
  const { execute } = useLazyQuery<{ categorySegment: ICategorySegment }>(
    FETCH_MINIMUM_RENTAL_DATE_QUERY
  )

  const validator = async (_rule: unknown, values: dayjs.Dayjs[]) => {
    const startDate = values?.[0]
    const returnDate = values?.[1]

    if (startDate?.isValid() && returnDate?.isValid()) {
      const { data } = await execute({
        variables: { id: member.id, startDate: startDate.format(dateFormat.base) }
      })

      const { minimumReturnDate } = data?.categorySegment || member || {}
      const minimumReturnDateDayjs = dayjs(minimumReturnDate, dateFormat.base)

      if (!minimumReturnDate || returnDate.isSameOrAfter(minimumReturnDateDayjs)) {
        return Promise.resolve()
      }
      return Promise.reject(MINIMUM_RENTAL_TIME_ERROR)
    }

    return Promise.reject('Invalid date range.')
  }

  return (
    <KFormItem
      rules={[{ validator }]}
      name="dates"
      hasFeedback={false}
      hideErrors={true}
      showLabel={false}>
      <MinimumRentalTimeWrapper member={member} {...others} />
    </KFormItem>
  )
}

const MinimumRentalTimeWrapper: React.FC<{ value?: Dayjs[] } & Props> = ({ member, value }) => {
  const { form } = useKFormContext()
  const [isValid, setIsValid] = useState(true)
  const startDate = value?.[0]
  const returnDate = value?.[1]

  useEffect(() => {
    form
      .validateFields([FIELD_NAME])
      .then(() => setIsValid(true))
      .catch((errorInfo: ErrorInfo) => {
        const errorField = errorInfo.errorFields.find(({ name }) => name.toString() === FIELD_NAME)
        if (!errorField) return

        const error = errorField.errors.find((error) => error === MINIMUM_RENTAL_TIME_ERROR)
        if (error) setIsValid(false)
      })
  }, [startDate, returnDate])

  return <MinimumRentalTime member={member} props={{ type: isValid ? undefined : 'danger' }} />
}

export default MinimumRentalTimeFormItem
